import { useTranslation } from 'react-i18next';

import { resistorMarking, type BandColor } from './resistorBands';

const SWATCH: Record<BandColor, string> = {
  black: '#111111',
  brown: '#7b4a12',
  red: '#c62828',
  orange: '#ef6c00',
  yellow: '#f9c000',
  green: '#2e7d32',
  blue: '#1565c0',
  violet: '#7b1fa2',
  grey: '#9e9e9e',
  white: '#f5f5f5',
  gold: '#c9a227',
  silver: '#b8b8b8',
};

/**
 * What this resistor looks like in the drawer.
 *
 * The catalogue knows the resistance and the drawer knows only the marking, so
 * the slow half of finding a resistor is converting between them in your head.
 * Both forms are shown because both are in the drawer: the printed code on the
 * chips and the colour bands on the through-hole ones.
 */
export function ResistorMarking({
  ohms,
  tolerance,
}: {
  ohms: number | null;
  tolerance?: string | null;
}) {
  const { t } = useTranslation();
  const marking = resistorMarking(ohms, tolerance);
  if (!marking) return null;

  const code = marking.chip ?? marking.chipFraction;

  return (
    <div className="marking">
      <span className="marking__label">{t('components.marking')}</span>
      {code && <span className="marking__code">{code}</span>}
      {marking.eia96 && (
        <span className="marking__code" title={t('components.markingEia96')}>
          {marking.eia96}
        </span>
      )}
      {marking.bands && (
        <span
          className="marking__bands"
          title={marking.bands.map((band) => t(`components.band.${band}`)).join(' · ')}
        >
          {marking.bands.map((band, index) => (
            <i key={index} style={{ background: SWATCH[band] }} />
          ))}
        </span>
      )}
    </div>
  );
}
